'use client';
import { TokenizeFileResponseBody } from 'bff';
import { FileTypes } from 'database';
import { IconUpload } from '@tabler/icons-react';
import { useCallback, useRef, useState } from 'react';
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'ui';
import { WretchError } from 'wretch/resolver';
import { useFileUpload } from '../hooks/useFileUpload';
import { UploadFile, UploadFileRef } from './UploadFile';

// Upload dialog shared by both sides of an order: the modal, the file state and
// the reset-on-close behaviour are identical, only the tokenizer and the upload
// endpoint differ, so both are injected.
export const UploadDocumentDialog = ({
  tokenizer,
  onUpload,
  onError,
  maxFiles = 5,
  triggerLabel = 'Subir documento',
  title = 'Subir documento',
  description = 'Adjuntá los documentos que querés sumar a la orden de trabajo.',
  submitLabel = 'Subir documentos',
  variant = 'default',
  fileTypes,
}: {
  tokenizer: (file: {
    name: string;
    size: number;
  }) => Promise<TokenizeFileResponseBody>;
  onUpload: (files: { token: string }[]) => Promise<unknown>;
  onError: (error: WretchError) => unknown;
  maxFiles?: number;
  triggerLabel?: string;
  title?: string;
  description?: string;
  submitLabel?: string;
  variant?: 'default' | 'outline' | 'link';
  fileTypes?: FileTypes[];
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const uploadRef = useRef<UploadFileRef>(null);
  const file = useFileUpload({ tokenizer, maxFiles, fileTypes });

  // Clears both the uploaded files (hook state) and the ones still
  // loading/pending (UploadFile's internal state), so they don't reappear when
  // the dialog is reopened.
  const resetFiles = useCallback(() => {
    file.setFiles([]);
    uploadRef.current?.reset();
  }, [file.setFiles]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);

      if (!next) resetFiles();
    },
    [resetFiles],
  );

  const onSubmit = useCallback(() => {
    if (file.files.length === 0) return;

    setIsLoading(true);
    onUpload(file.files.map(({ token }) => ({ token })))
      .then(() => {
        // `setOpen(false)` is programmatic → it does not fire `onOpenChange`,
        // so the reset has to happen here too.
        setOpen(false);
        resetFiles();
      })
      .catch(onError)
      .finally(() => setIsLoading(false));
  }, [file.files, onError, onUpload, resetFiles]);

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogTrigger asChild>
        <Button
          size='sm'
          className={cn(
            'h-8 rounded-2xl shrink-0 whitespace-nowrap',
            variant === 'outline' && 'border border-gray-200 hover:bg-gray-100',
            variant === 'link' && 'text-dark',
          )}
          variant={variant}
        >
          {variant === 'link' && <IconUpload className='h-4 w-4 mr-1' />}
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {/* @ts-ignore */}
        <UploadFile ref={uploadRef} {...file} />
        <DialogFooter>
          <Button
            size='sm'
            onClick={onSubmit}
            loading={isLoading}
            disabled={file.files.length === 0}
          >
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
